import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import {
  apiClient,
  PropertyTableLoaderResult,
  UnitPlanTableLoaderResult,
} from "~/services/apiClient";
import { getCachedAuthAccessInfo, loadCookiesData } from "~/utils";
import {
  createClientFilteredShouldRevalidate,
  setLoaderQueryData,
  STALE_TIME_TIER_A,
} from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";

export type UnitPlanTablePageLoaderType = {
  unitPlanList: UnitPlanTableLoaderResult["unitPlanList"];
  propertyNameById: Record<string, string>;
};

export function unitPlanTableQueryKey(
  ownerID: string,
  ownerType: APITypes.OwnerType | "",
) {
  return ["unit-plan-table", ownerID, ownerType] as const;
}

export const unitPlanTableShouldRevalidate = createClientFilteredShouldRevalidate(
  (owner) => unitPlanTableQueryKey(owner.ownerID, owner.ownerType),
  STALE_TIME_TIER_A,
);

export async function fetchUnitPlanTableData(): Promise<UnitPlanTablePageLoaderType> {
  const user = await getReadyAuthUser();
  if (!user) {
    throw new Error("Unauthenticated");
  }
  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) {
    throw new Error("Failed to load auth context");
  }
  const { ownerID, ownerType } = authAccessInfo.data;

  const [plansResult, propertiesResult] = await Promise.all([
    apiClient.loader<UnitPlanTableLoaderResult>("unit-plan-table", {
      ownerID,
      ownerType,
      userID: user.uid,
    }),
    apiClient.loader<PropertyTableLoaderResult>("property-table", {
      ownerID,
      ownerType,
    }),
  ]);

  if (!plansResult.success || !plansResult.data) {
    throw new Error(plansResult.error ?? "Failed to load unit plans");
  }

  const propertyNameById: Record<string, string> = {};
  for (const property of propertiesResult.data?.properties ?? []) {
    propertyNameById[property.id] = property.name;
  }

  return {
    unitPlanList: plansResult.data.unitPlanList,
    propertyNameById,
  };
}

export async function UnitPlanTablePageLoader({
  request,
}: LoaderFunctionArgs): Promise<UnitPlanTablePageLoaderType | Response> {
  void request;
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const data = await fetchUnitPlanTableData();
  const auth = getCachedAuthAccessInfo();
  if (auth) {
    setLoaderQueryData(
      unitPlanTableQueryKey(auth.ownerID, auth.ownerType),
      data,
      STALE_TIME_TIER_A,
    );
  } else {
    setLoaderQueryData(unitPlanTableQueryKey("", ""), data, STALE_TIME_TIER_A);
  }

  return data;
}
